import type { ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Tabs } from '@xdocs/ui';
import type { TabItem } from '@xdocs/ui';
import { useSettingsT } from './useSettingsT.js';

type SettingsTabPath =
  | '/settings'
  | '/settings/billing'
  | '/settings/sessions'
  | '/settings/drive'
  | '/settings/notifications'
  | '/settings/referrals';

/**
 * Shared tab strip rendered at the top of every `/settings/*` page this task
 * owns (F10 "settings nav: Profile, Plan & Billing, Sessions, Drive"; visual
 * spec: design/app/27-settings.html's accent-underline `settingsTabs` row).
 * Each section is its own deep-linkable route (deliberately NOT the
 * mockup's local-state tab switch — see `WIRING.md` §1 for why routes were
 * chosen), so this wraps the shared `<Tabs>` primitive (CLAUDE_DESIGN.md
 * §6.5, the same accent-underline row `UserDetailPage` uses) with
 * `value`/`onChange` bound to the current path instead of component state —
 * same ARIA `tablist` pattern and pixel styling, real URLs underneath.
 * A plain component (not a layout route) — `App.tsx`/router wiring is out
 * of ownership (see `WIRING.md`), so each page composes this directly
 * rather than relying on a shared `<Outlet>` layout route.
 */
export function SettingsNav(): ReactElement {
  const { t } = useSettingsT();
  const location = useLocation();
  const navigate = useNavigate();

  // Order matches design/app/27-settings.html's tab strip (Profile, Plan &
  // Billing, Referrals, Sessions, Notifications, Drive) — mock's tab strip
  // also has a 7th "Web Push" tab with no route of its own here; that
  // control lives inside `/settings/notifications` (`PushSettingsSection`,
  // reused rather than duplicated per that page's doc comment) instead of
  // getting a dedicated top-level tab, since adding one needs a new route
  // wired in `App.tsx` (shared router, out of this task's ownership).
  const items: TabItem<SettingsTabPath>[] = [
    { value: '/settings', label: t('settings:nav.profile') },
    { value: '/settings/billing', label: t('settings:nav.billing') },
    { value: '/settings/referrals', label: t('settings:nav.referrals') },
    { value: '/settings/sessions', label: t('settings:nav.sessions') },
    { value: '/settings/notifications', label: t('settings:nav.notifications') },
    { value: '/settings/drive', label: t('settings:nav.drive') },
  ];

  const active = items.find((item) => item.value === location.pathname)?.value ?? '/settings';

  return (
    <Tabs<SettingsTabPath>
      label={t('settings:nav.label')}
      value={active}
      onChange={(value) => void navigate(value)}
      items={items}
    />
  );
}
